import { watch, computed, reactive } from "vue";
import { useAppRegistration } from "@/stores/app-registration";
import { storeToRefs } from "pinia";
import { bignumber, add, multiply, divide, round } from "mathjs";
import { useApp } from "@/stores/app";

export function usePriceOverview(ticket, purchaseItems = []) {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("nl-NL", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value));
  };

  const appStore = useApp();
  const appRegistrationStore = useAppRegistration();

  function addToNestedObject(obj, key, nestedKey, value) {
    if (
      typeof obj[key] !== "object" ||
      obj[key] === null ||
      Array.isArray(obj[key])
    ) {
      obj[key] = {}; // overwrite if not a plain object
    }
    obj[key][nestedKey] = value;
  }

  const { registrationValues, fields } = storeToRefs(appRegistrationStore);

  // Use computed for addons calculation based on registration values
  const selectedAddons = computed(() => {
    const selectedAddons_ = [];
    for (const field_key of Object.keys(addons.value)) {
      const selectedValue = registrationValues.value[field_key];

      //Multiple choice
      if (Array.isArray(selectedValue)) {
        // If the selected value is not present in the addons, filter it out.
        const validOptions = selectedValue.filter(
          (item) => addons.value[field_key] && addons.value[field_key][item.id]
        );

        // If no valid options remain, skip this field.
        if (validOptions.length === 0) continue;

        selectedAddons_.push(
          ...validOptions.map((item) => {
            return addons.value[field_key][item.id];
          })
        );
      } else {
        // Workshop
        if (!selectedValue) continue;

        // If the selected value is not present in the addons, skip it.
        if (!addons.value[field_key]) continue;
        if (!addons.value[field_key][selectedValue]) continue;

        selectedAddons_.push(addons.value[field_key][selectedValue]);
      }
    }
    return selectedAddons_;
  });

  const addons = computed(() => {
    const addons_ = {};

    for (const each_field of fields.value) {
      if (each_field.type === "workshop") {
        for (const each_workshop of each_field.workshops) {
          // If the workshop amount is not a number, skip it
          if (isNaN(each_workshop.amount)) {
            continue;
          }
          if (each_workshop.amount > 0) {
            addToNestedObject(
              addons_,
              each_field.handle,
              each_workshop.workshop_id,
              each_workshop
            );
          }
        }
      }

      if (each_field.type === "multiplechoice") {
        for (const each_option of each_field.options) {
          // If the option amount is not a number, skip it
          if (isNaN(each_option.amount)) {
            continue;
          }
          if (each_option.amount > 0) {
            addToNestedObject(
              addons_,
              each_field.handle,
              each_option.id,
              each_option
            );
          }
        }
      }
    }
    return addons_;
  });

  // Calculate price overview based on selected addons
  const priceOverviewComputed = computed(() => {
    const addonsToProcess = purchaseItems.length
      ? purchaseItems
      : selectedAddons.value;
    const processedAddons = [];

    let total = bignumber(ticket.price.real);

    for (const item of addonsToProcess) {
      // If the item amount is not a number, skip it
      if (!item || isNaN(item.amount)) {
        continue;
      }
      // If the item vat is not a number, skip it
      if (isNaN(item.vat)) {
        continue;
      }

      // The amount from the backend includes the vat, so no need to calculate the VAT again.
      const amountIncludingTax = bignumber(item.amount);

      // Store as formatted euro string
      const processedItem = {
        ...item,
        amountIncludingTax: formatCurrency(
          round(amountIncludingTax, 2).toString()
        ),
      };

      processedAddons.push(processedItem);
      total = add(total, amountIncludingTax);
    }

    let serviceFee = formatCurrency(round(0, 2).toString());
    let finalTotal = total;

    if (!appStore.event.ticketing_fee_included) {
      const feeWithoutTax = divide(multiply(total, 3), 100);
      const fee = add(feeWithoutTax, divide(multiply(feeWithoutTax, 21), 100));
      serviceFee = formatCurrency(round(fee, 2).toString());
      finalTotal = add(total, fee);
    }

    return {
      serviceFee,
      total: formatCurrency(round(finalTotal, 2).toString()),
      addons: processedAddons,
    };
  });

  // Create a reactive object that stays in sync with the computed
  const priceOverview = reactive({
    serviceFee: formatCurrency(round(0.0, 2).toString()),
    total: formatCurrency(round(0.0, 2).toString()),
    addons: [],
  });

  // Watch the computed and update the reactive object
  watch(
    priceOverviewComputed,
    (newValue) => {
      priceOverview.serviceFee = newValue.serviceFee;
      priceOverview.total = newValue.total;
      priceOverview.addons = newValue.addons;
    },
    { immediate: true }
  );

  return { priceOverview };
}
